import React, { useState } from 'react';
import { Eye, EyeOff, Lock, Shield, Loader } from 'lucide-react';
import { verifyViewPermission, logDataAccess, getCurrentAdminInfo } from '../utils/backendIntegration.ts';

interface ProtectedDataProps {
  label: string;
  value: string;
  type?: 'phone' | 'email' | 'name';
  onAccess?: (type: string, value: string) => void;
  dogId?: string;
  ownerId?: string;
  pageContext?: string;
}

const ProtectedData: React.FC<ProtectedDataProps> = React.memo(({
  label,
  value,
  type = 'phone',
  onAccess,
  dogId,
  ownerId,
  pageContext
}) => {
  // Debug log removed for performance
  
  // React hooks must be called before any early returns
  const [isVisible, setIsVisible] = useState(false);
  const [isCheckingPassword, setIsCheckingPassword] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');

  // Prevent page reload when modal is open
  React.useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (showPasswordModal && e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        
        setShowPasswordModal(false);
        setPassword('');
        setError('');
      }
    };

    const handleClickOutside = (e: MouseEvent) => {
      // Modal dışına tıklanırsa modalı kapatma - bu şekilde state bozulmaz
      const target = e.target as HTMLElement;
      if (showPasswordModal && !target.closest('.protected-data-modal')) {
        // İptal etmiyoruz - kullanıcı İptal butonuna basmalı
      }
    };

    if (showPasswordModal) {
      document.addEventListener('keydown', handleKeyDown);
      document.addEventListener('click', handleClickOutside);
      document.body.style.overflow = 'hidden'; // Prevent scrolling
      // Modal açıldı - DOM body overflow hidden
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('click', handleClickOutside);
      document.body.style.overflow = 'unset';
      // Modal kapandı - DOM body overflow reset
    };
  }, [showPasswordModal]);

  // Modal state tracking removed for performance

  // Show empty state if no value
  if (!value || value.trim() === '') {
    return (
      <div className="space-y-2">
        <div className="relative overflow-hidden rounded-xl border-2 border-gray-200 bg-gray-50 p-4">
          <div className="flex items-center space-x-2 mb-2">
            <div className="w-6 h-6 rounded-full flex items-center justify-center bg-red-100">
              <Shield size={14} className="text-red-600" />
            </div>
            <label className="text-sm font-semibold text-gray-700">{label}</label>
          </div>
          <span className="text-sm text-gray-500 italic">
            {type === 'name' ? 'Sahip adı eksik' : `${label} bilgisi bulunamadı`}
          </span>
        </div>
      </div>
    );
  }

  // Mask the value - show only first 2-3 chars and last 2 chars
  const maskValue = (val: string, dataType: string) => {
    if (!val) return '-';
    
    switch (dataType) {
      case 'phone':
        // 0555 123 45 67 -> 0555 *** ** 67
        if (val.length > 6) {
          return val.substring(0, 4) + ' *** ** ' + val.substring(val.length - 2);
        }
        return val.substring(0, 2) + '*'.repeat(val.length - 2);
      
      case 'email':
        // john@example.com -> j***@ex***le.com
        const [username, domain] = val.split('@');
        if (username.length > 2 && domain) {
          const maskedUsername = username[0] + '*'.repeat(username.length - 2) + username[username.length - 1];
          const domainParts = domain.split('.');
          if (domainParts.length >= 2) {
            const lastNamePart = domainParts[domainParts.length - 1];
            const maskedDomain = domainParts[0].substring(0, 2) + '*'.repeat(domainParts[0].length - 2) + '.' + lastNamePart;
            return `${maskedUsername}@${maskedDomain}`;
          }
        }
        return val[0] + '*'.repeat(val.length - 1);
      
      case 'name':
        // İsim maskelenmeyecek - direkt göster
        return val;
      
      default:
        return '*'.repeat(Math.min(val.length, 8));
    }
  };

  // Verify password
  const handlePasswordCheck = async () => {
    // Password check started
    
    if (!password.trim()) {
      setError('Şifre boş olamaz');
      return;
    }

    setIsCheckingPassword(true);
    setError('');

    try {
      // API call in progress
      
      // Real backend API call for password verification
      const result = await verifyViewPermission({
        password,
        dataType: type,
        dataValue: value,
        userId: dogId || ownerId,
        pageContext: pageContext || 'admin-panel',
        timestamp: new Date().toISOString()
      });

      if (result.success) {
        setIsVisible(true);
        setShowPasswordModal(false);
        setPassword('');
        onAccess?.(type, value);
        
        // Log successful access
        const adminInfo = getCurrentAdminInfo();
        await logDataAccess({
          adminId: adminInfo?.id || 'unknown',
          adminEmail: adminInfo?.email || 'unknown',
          dataType: type,
          dataValue: value,
          dogId: dogId || undefined,
          ownerId: ownerId || undefined,
          sessionId: result.sessionId,
          pageContext: pageContext || 'admin-panel'
        });
        
        // Auto-hide after 30 seconds
        setTimeout(() => {
          setIsVisible(false);
        }, 30000);
      } else {
        setError(result.message || 'Şifre doğrulaması başarısız');
      }
    } catch (err) {
      // Password verification error
      
      // API hatası durumunda kullanıcı dostu mesaj
      if (err.message && err.message.includes('fetch')) {
        setError('Backend server ile bağlantı kurulamadı. Lütfen tekrar deneyin.');
      } else if (err.message && err.message.includes('404')) {
        setError('API endpoint bulunamadı. Lütfen yönetici ile iletişime geçin.');
      } else if (err.message && err.message.includes('500')) {
        setError('Sunucu hatası. Lütfen tekrar deneyin.');
      } else {
        setError('Şifre doğrulaması sırasında beklenmeyen hata oluştu.');
      }
    } finally {
      setIsCheckingPassword(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !isCheckingPassword) {
      handlePasswordCheck();
    }
  };

  return (
    <div className="space-y-2">
      <div className={`group relative overflow-hidden rounded-xl border-2 transition-all duration-200 ${
        isVisible 
          ? 'bg-gradient-to-r from-green-50 to-emerald-50 border-green-300 shadow-md' 
          : (pageContext === 'oteldeki-kopekler' || pageContext === 'kopek-durum-guncellemeleri' || pageContext === 'gecmis-ziyaretler')
            ? 'bg-white border-gray-200 hover:border-orange-300 hover:shadow-sm'
            : 'bg-white border-gray-200 hover:border-blue-300 hover:shadow-sm'
      }`}>
        {/* Blur overlay for masked data - only for phone and email */}
        {!isVisible && type !== 'name' && (
          <div className="absolute inset-0 bg-gradient-to-r from-gray-100 via-transparent to-gray-100 opacity-30 pointer-events-none"></div>
        )}
        
        <div className="relative p-4">
          <div className="flex items-center justify-between">
            <div className="flex-1 min-w-0">
              <div className="flex items-center space-x-2 mb-2">
                <div className={`w-6 h-6 rounded-full flex items-center justify-center ${
                  isVisible 
                    ? 'bg-green-100' 
                    : (pageContext === 'oteldeki-kopekler' || pageContext === 'kopek-durum-guncellemeleri' || pageContext === 'gecmis-ziyaretler')
                      ? 'bg-orange-100'
                      : 'bg-blue-100'
                }`}>
                  <Shield size={14} className={
                    isVisible 
                      ? 'text-green-600' 
                      : (pageContext === 'oteldeki-kopekler' || pageContext === 'kopek-durum-guncellemeleri' || pageContext === 'gecmis-ziyaretler')
                        ? 'text-orange-600'
                        : 'text-blue-600'
                  } />
                </div>
                <label className="text-sm font-semibold text-gray-700">{label}</label>
              </div>
              
              <div className={`flex items-center space-x-2 min-w-0 ${
                isVisible || type === 'name' ? 'opacity-100' : 'opacity-70'
              }`}>
                {type === 'phone' && <div className="w-4 h-4 bg-gray-400 rounded-sm flex items-center justify-center">
                  <span className="text-white text-xs font-bold">📱</span>
                </div>}
                {type === 'email' && <div className="w-4 h-4 bg-gray-400 rounded-sm flex items-center justify-center">
                  <span className="text-white text-xs font-bold">✉</span>
                </div>}
                {type === 'name' && <div className="w-4 h-4 bg-gray-400 rounded-sm flex items-center justify-center">
                  <span className="text-white text-xs font-bold">👤</span>
                </div>}
                
                <span className={`text-sm truncate ${
                  isVisible || type === 'name'
                    ? 'text-gray-900 font-medium' 
                    : 'text-gray-500 font-mono tracking-wider'
                }`}>
                  {isVisible || type === 'name' ? value : maskValue(value, type)}
                </span>
              </div>
              
              {isVisible && type !== 'name' && (
                <div className="mt-2 text-xs text-green-600 bg-green-100 inline-flex items-center px-2 py-1 rounded-full">
                  <div className="w-2 h-2 bg-green-500 rounded-full mr-1.5 animate-pulse"></div>
                  Görünür (30s sonra gizlenecek)
                </div>
              )}
            </div>
            
            <div className="ml-4 flex-shrink-0">
              {type === 'name' ? (
                // İsim için gurvenlik butonu yok - direkt gösteriliyor
                <div className="w-4 h-4 bg-green-100 rounded-full flex items-center justify-center">
                  <Shield size={14} className="text-green-600" />
                </div>
              ) : (
                !isVisible ? (
                  <button
                    onClick={() => {
                      // View button clicked
                      setShowPasswordModal(true);
                    }}
                    className={`group/btn text-white px-4 py-2 rounded-lg transition-all shadow-md hover:shadow-lg text-sm font-medium flex items-center space-x-2 ${
                      (pageContext === 'oteldeki-kopekler' || pageContext === 'kopek-durum-guncellemeleri' || pageContext === 'gecmis-ziyaretler')
                        ? 'bg-gradient-to-r from-orange-500 to-amber-600 hover:from-orange-600 hover:to-amber-700'
                        : 'bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800'
                    }`}
                  >
                    <Eye size={14} />
                    <span>Görüntüle</span>
                  </button>
                ) : (
                  <button
                    onClick={() => setIsVisible(false)}
                    className="bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 transition-colors text-sm font-medium flex items-center space-x-2"
                  >
                    <EyeOff size={14} />
                    <span>Gizle</span>
                  </button>
                )
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Password Modal */}
      {showPasswordModal && !isCheckingPassword && (
        <div 
          className="protected-data-modal fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4" 
          style={{ 
            zIndex: 99999,
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            backgroundColor: 'rgba(0,0,0,0.5)'
          }}
        >
          <div 
            className="bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 transform transition-all overflow-hidden" 
            style={{ 
              position: 'relative',
              zIndex: 100000
            }}
          >
            {/* Modal Header */}
            <div className={`px-6 py-4 ${
              (pageContext === 'oteldeki-kopekler' || pageContext === 'kopek-durum-guncellemeleri' || pageContext === 'gecmis-ziyaretler')
                ? 'bg-gradient-to-r from-orange-500 to-amber-600'
                : 'bg-gradient-to-r from-blue-600 to-blue-700'
            }`}>
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-white bg-opacity-20 rounded-full flex items-center justify-center">
                  <Shield size={20} className="text-white" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-white">Güvenlik Doğrulaması</h3>
                  <p className={`text-sm ${
                    (pageContext === 'oteldeki-kopekler' || pageContext === 'kopek-durum-guncellemeleri' || pageContext === 'gecmis-ziyaretler') ? 'text-orange-100' : 'text-blue-100'
                  }`}>Bu bilgiyi görüntülemek için şifrenizi girin</p>
                </div>
              </div>
            </div>
            
            {/* Modal Body */}
            <div className="px-6 py-6 space-y-4">
              {/* Data Type Info */}
              <div className={`bg-gray-50 rounded-lg p-4 border-l-4 ${
                (pageContext === 'oteldeki-kopekler' || pageContext === 'kopek-durum-guncellemeleri' || pageContext === 'gecmis-ziyaretler') ? 'border-orange-500' : 'border-blue-500'
              }`}>
                <div className="flex items-center space-x-2">
                  <Lock size={16} className={
                    (pageContext === 'oteldeki-kopekler' || pageContext === 'kopek-durum-guncellemeleri' || pageContext === 'gecmis-ziyaretler') ? 'text-orange-600' : 'text-blue-600'
                  } />
                  <span className="text-sm font-medium text-gray-700">{label}</span>
                </div>
                <p className="text-xs text-gray-500 mt-1">Gizli bilgi • Access logging aktif</p>
              </div>
              
              {/* Password Input */}
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">
                  Admin Şifresi
                </label>
                <div className="relative">
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    onKeyPress={handleKeyPress}
                    placeholder="Şifrenizi girin"
                    className={`w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:border-transparent text-sm transition-colors ${
                      (pageContext === 'oteldeki-kopekler' || pageContext === 'kopek-durum-guncellemeleri' || pageContext === 'gecmis-ziyaretler')
                        ? 'focus:ring-orange-500'
                        : 'focus:ring-blue-500'
                    }`}
                    autoFocus
                  />
                  <div className="absolute right-3 top-3">
                    <Lock size={16} className="text-gray-400" />
                  </div>
                </div>
                
                {error && (
                  <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg">
                    <div className="flex items-center space-x-2">
                      <div className="w-4 h-4 bg-red-500 rounded-full flex items-center justify-center">
                        <span className="text-white text-xs">×</span>
                      </div>
                      <span className="text-sm font-medium">{error}</span>
                    </div>
                  </div>
                )}
              </div>
              
              {/* Security Notice */}
              <div className={`rounded-lg p-3 ${
                (pageContext === 'oteldeki-kopekler' || pageContext === 'kopek-durum-guncellemeleri' || pageContext === 'gecmis-ziyaretler')
                  ? 'bg-gradient-to-r from-orange-50 to-amber-50 border border-orange-200'
                  : 'bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200'
              }`}>
                <div className="flex items-center space-x-2">
                  <div className={`w-5 h-5 rounded-full flex items-center justify-center ${
                    (pageContext === 'oteldeki-kopekler' || pageContext === 'kopek-durum-guncellemeleri' || pageContext === 'gecmis-ziyaretler') ? 'bg-orange-500' : 'bg-blue-500'
                  }`}>
                    <Shield size={12} className="text-white" />
                  </div>
                  <div className="flex-1">
                    <p className={`text-xs font-medium ${
                      (pageContext === 'oteldeki-kopekler' || pageContext === 'kopek-durum-guncellemeleri' || pageContext === 'gecmis-ziyaretler') ? 'text-orange-800' : 'text-blue-800'
                    }`}>Güvenli Erişim</p>
                    <p className={`text-xs ${
                      (pageContext === 'oteldeki-kopekler' || pageContext === 'kopek-durum-guncellemeleri' || pageContext === 'gecmis-ziyaretler') ? 'text-orange-700' : 'text-blue-700'
                    }`}>Bu işlem log'a kaydedilecektir</p>
                  </div>
                </div>
              </div>
              
              {/* Action Buttons */}
              <div className="flex space-x-3 pt-2">
                <button
                  onClick={() => {
                    // Modal cancelled - state preserved
                    setShowPasswordModal(false);
                    setPassword('');
                    setError('');
                    // Parent state'i korumak için setTimeout ile DOM update'i geciktir
                    setTimeout(() => {
                      // Modal state cleared
                    }, 100);
                  }}
                  className="flex-1 px-4 py-3 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors font-medium text-sm"
                >
                  İptal
                </button>
                <button
                  onClick={handlePasswordCheck}
                  disabled={!password.trim()}
                  className={`flex-1 text-white py-3 px-4 rounded-lg disabled:from-gray-400 disabled:to-gray-400 disabled:cursor-not-allowed transition-all font-medium text-sm shadow-md ${
                    (pageContext === 'oteldeki-kopekler' || pageContext === 'kopek-durum-guncellemeleri' || pageContext === 'gecmis-ziyaretler')
                      ? 'bg-gradient-to-r from-orange-500 to-amber-600 hover:from-orange-600 hover:to-amber-700'
                      : 'bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800'
                  }`}
                >
                  Doğrula
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Loading State */}
      {isCheckingPassword && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4" style={{ zIndex: 9999 }}>
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-sm mx-4 p-6">
            <div className="text-center space-y-4">
              <div className={`w-16 h-16 rounded-full flex items-center justify-center mx-auto ${
                (pageContext === 'oteldeki-kopekler' || pageContext === 'kopek-durum-guncellemeleri' || pageContext === 'gecmis-ziyaretler') ? 'bg-orange-100' : 'bg-blue-100'
              }`}>
                <Loader size={24} className={`animate-spin ${
                  (pageContext === 'oteldeki-kopekler' || pageContext === 'kopek-durum-guncellemeleri' || pageContext === 'gecmis-ziyaretler') ? 'text-orange-600' : 'text-blue-600'
                }`} />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Doğrulanıyor</h3>
                <p className="text-sm text-gray-600">Şifre kontrol ediliyor, lütfen bekleyin...</p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
});

ProtectedData.displayName = 'ProtectedData';

export default ProtectedData;
